import React from "react";
import "./add-merchant-form.css";
import CustomModal from "components/custom-modal/CustomModal";
import CustomForm from "components/custom-form/CustomForm";
import Button from "components/button/Button";
import { XIconV1 } from "images/image-component";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import { useAddMerchant } from "./useAddMerchant";
import type { AddMerchantStep } from "./useAddMerchant";
import { formatCountdown } from "hooks/useEmailOtp";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** When set, resume signup for this already-on-chain merchant (starts at step 2). */
  resumeMerchantId?: string;
  /** Called after a successful signup so the caller can refresh the list. */
  onCompleted?: () => void;
}

const STEP_COPY: Record<
  AddMerchantStep,
  { title: string; subtitle: string }
> = {
  1: {
    title: "Add New Merchant",
    subtitle: "Create the merchant account.",
  },
  2: {
    title: "Merchant Signup",
    subtitle: "Complete the merchant’s signup details.",
  },
  3: {
    title: "Verify Email",
    subtitle: "Enter the code sent to the merchant’s inbox.",
  },
};

function AddMerchantForm({
  isOpen,
  onClose,
  resumeMerchantId,
  onCompleted,
}: Props) {
  const {
    step,
    totalSteps,
    isResume,
    fields,
    submitRef,
    submitLabel,
    isSubmitDisabled,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    canRetry,
    showSuccess,
    successMsg,
    noticeMsg,
    canResend,
    secondsLeft,
    handleResend,
    handleSubmit,
    handleRetry,
    handleBack,
    handleCloseError,
    handleCloseSuccess,
    reset,
  } = useAddMerchant(resumeMerchantId);

  // Block closing on a FRESH step 2 — the merchant is already registered
  // on-chain but not yet signed up, so abandoning here leaves them unusable.
  // Step 1 (nothing created) and resume mode (already recoverable via the row's
  // Sign up button) stay closable.
  const canClose = step === 1 || isResume;

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSuccessClose = () => {
    handleCloseSuccess();
    reset();
    onClose();
    onCompleted?.();
  };

  const { title, subtitle } = STEP_COPY[step];

  return (
    <>
      <CustomModal isOpen={isOpen} handleClose={canClose ? handleClose : undefined}>
        {canClose && (
          <button
            className="v1-modal-close"
            aria-label="Close"
            onClick={handleClose}
            disabled={showLoading}
          >
            <XIconV1 />
          </button>
        )}

        <div className="amf-header">
          {!isResume && (
            <span className="amf-step">
              Step {step} of {totalSteps}
            </span>
          )}
          <h2 className="v1-modal-title">{title}</h2>
          <p className="v1-modal-subtitle">{subtitle}</p>
        </div>

        <CustomForm fields={fields} />

        {noticeMsg && <p className="amf-notice">{noticeMsg}</p>}

        {step === 3 && (
          <div className="amf-resend">
            {canResend ? (
              <button
                type="button"
                className="amf-resend-btn"
                onClick={handleResend}
                disabled={showLoading}
              >
                Didn't get the code? Resend
              </button>
            ) : (
              <span className="amf-resend-wait">
                You can request a new code in {formatCountdown(secondsLeft)}
              </span>
            )}
          </div>
        )}

        <div className="v1-modal-actions">
          {handleBack && (
            <Button variant="ghost" onClick={handleBack} disabled={showLoading}>
              Back
            </Button>
          )}
          {canClose && (
            <Button variant="ghost" onClick={handleClose} disabled={showLoading}>
              Cancel
            </Button>
          )}
          <Button
            ref={submitRef}
            onClick={handleSubmit}
            disabled={isSubmitDisabled || showLoading}
          >
            {submitLabel}
          </Button>
        </div>
      </CustomModal>

      <LoadingModal isOpen={showLoading} loadingMsg={loadingMsg} />

      <ErrorModal
        isOpen={showError}
        errorMsg={errorMsg}
        handleClose={handleCloseError}
        tryAgain={canRetry ? handleRetry : undefined}
      />

      <SuccessModal
        isOpen={showSuccess}
        message={successMsg}
        handleClose={handleSuccessClose}
      />
    </>
  );
}

export default AddMerchantForm;
